import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Boxes, CalendarDays, ChevronLeft, Download, FileSpreadsheet, Upload } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import {
  AppButton, AppModal, Callout, FileUpload, FormField, Radio, Tabs, TabsContent, TabsList,
  TabsTrigger, Textarea,
} from "../../ui";
import { importFromText, linkImpact, mergeImport, LIMITS } from "./syllabusImport";
import { ACCEPTED_EXTENSIONS, MAX_FILE_MB, downloadTemplate, importWorkbook } from "./syllabusWorkbook";

const FORMAT_LABEL = { module: "Module → Topic → Subtopic", day: "Day-wise" };
const MAX_LISTED = 50;

const PASTE_PLACEHOLDER = `Paste cells copied from Excel / Google Sheets, or an outline:

Module 1: Core Java
  1. Introduction (60 min)
     - JDK, JRE and JVM
     - Your first program
  2. Variables and Data Types
Module 2: Object-Oriented Programming
  1. Classes and Objects (1.5h)`;

const formatMinutes = (mins) => {
  if (!mins) return "0m";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
};

/** "Row 12" / "Line 12" in front of a message, or nothing for a file-wide one. */
function IssueList({ items, unit }) {
  const shown = items.slice(0, MAX_LISTED);
  return (
    <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0 tw-text-[13px]">
      {shown.map((it, i) => (
        <li key={i}>
          {it.row != null && <strong className="tw-font-semibold">{unit === "line" ? "Line" : "Row"} {it.row}: </strong>}
          {it.message}
        </li>
      ))}
      {items.length > MAX_LISTED && <li>…and {items.length - MAX_LISTED} more.</li>}
    </ul>
  );
}

/** A read-only outline of what will load, collapsed per module/day. */
function PreviewTree({ result }) {
  const isDay = result.format === "day";
  const groups = isDay ? result.days : result.modules;
  return (
    <div className="tw-max-h-72 tw-overflow-y-auto tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-raised">
      {groups.map((g) => (
        <details key={isDay ? g.dayNumber : g.moduleNumber} className="tw-border-0 tw-border-b tw-border-solid tw-border-line-soft last:tw-border-b-0">
          <summary className="tw-flex tw-cursor-pointer tw-items-baseline tw-gap-2 tw-px-3 tw-py-2 tw-text-[13px]">
            <span className="tw-shrink-0 tw-font-semibold tw-text-content-muted">
              {isDay ? `Day ${g.dayNumber}` : `Module ${g.moduleNumber}`}
            </span>
            <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-font-medium tw-text-content">{g.title}</span>
            <span className="tw-shrink-0 tw-text-xs tw-text-content-subtle">
              {g.topics.length} topic{g.topics.length === 1 ? "" : "s"}
              {isDay && ` · ${formatMinutes(g.estimatedDuration)}`}
            </span>
          </summary>
          <ol className="tw-m-0 tw-flex tw-flex-col tw-gap-1 tw-pb-2.5 tw-pl-10 tw-pr-3 tw-text-[13px] tw-text-content">
            {g.topics.map((t, i) => (
              <li key={i}>
                {isDay ? t : (
                  <>
                    <span>{t.title}</span>
                    <span className="tw-text-xs tw-text-content-subtle"> · {formatMinutes(t.estimatedDuration)}</span>
                    {t.subtopics.length > 0 && (
                      <div className="tw-text-xs tw-text-content-muted">{t.subtopics.join(" · ")}</div>
                    )}
                  </>
                )}
              </li>
            ))}
          </ol>
        </details>
      ))}
    </div>
  );
}

/**
 * Settings → Course → Syllabus → Import.
 *
 * Reads a spreadsheet or pasted text, shows exactly what it understood (and
 * every row it could not), and hands the result to the editor. It never saves:
 * the admin reviews it in the editor and presses Save & Activate, the same as
 * an AI draft.
 *
 * Before a REPLACE it shows which materials and Smart Sessions would be
 * re-pointed. They link by day/module NUMBER and topic position, so a file that
 * reorders modules silently moves them otherwise. The comparison is against the
 * ACTIVE saved template — that is what those links were made against — not
 * whatever is unsaved in the editor.
 */
export default function SyllabusImportModal({
  show, onHide, courseId, currentFormat, current, savedTemplate, onApply,
}) {
  const modeName = useId();
  const [source, setSource] = useState("file");
  const [files, setFiles] = useState([]);
  const [pasted, setPasted] = useState("");
  const [reading, setReading] = useState(false);
  const [result, setResult] = useState(null);
  const [sourceLabel, setSourceLabel] = useState("");
  const [mode, setMode] = useState("replace");
  const [links, setLinks] = useState(null);

  // Fresh every time it opens — a stale preview from last time is a trap.
  useEffect(() => {
    if (!show) return;
    setFiles([]);
    setPasted("");
    setResult(null);
    setSourceLabel("");
    setMode("replace");
    setLinks(null);
    let cancelled = false;
    apiClient.get(`/syllabus/template/${courseId}/links`)
      .then(({ data }) => { if (!cancelled) setLinks(data); })
      .catch(() => { if (!cancelled) setLinks(false); });
    return () => { cancelled = true; };
  }, [show, courseId]);

  const readFile = useCallback(async (picked) => {
    setFiles(picked);
    const file = picked[0];
    if (!file) return;
    setReading(true);
    try {
      const buffer = await file.arrayBuffer();
      const r = importWorkbook(buffer);
      setResult(r);
      setSourceLabel(r.sheetName ? `${file.name} · sheet "${r.sheetName}"` : file.name);
    } catch {
      setResult({ error: "That file couldn't be read. Save it as .xlsx or .csv and try again." });
    } finally {
      setReading(false);
    }
  }, []);

  const readPasted = () => {
    setResult(importFromText(pasted));
    setSourceLabel("Pasted text");
  };

  const reset = () => {
    setResult(null);
    setFiles([]);
  };

  const imported = result && !result.error ? (result.format === "day" ? result.days : result.modules) : null;
  const existing = useMemo(
    () => (result ? (result.format === "day" ? current.days : current.modules) : []),
    [result, current.days, current.modules],
  );
  const canAppend = !!imported && result.format === currentFormat && existing.length > 0;
  const effectiveMode = canAppend ? mode : "replace";
  const next = useMemo(
    () => (imported ? mergeImport(existing, imported, effectiveMode, result.format) : null),
    [imported, existing, effectiveMode, result],
  );

  const combinedLimit = result?.format === "day" ? LIMITS.days : LIMITS.modules;
  const overLimit = !!next && next.length > combinedLimit;

  const impact = useMemo(() => {
    if (!next || !links || !savedTemplate) return [];
    const savedFormat = savedTemplate.syllabusType === "module" ? "module" : "day";
    return linkImpact({
      current: savedFormat === "day" ? savedTemplate.days : savedTemplate.modules,
      next: savedFormat === result.format ? next : null,
      format: savedFormat,
      links,
    });
  }, [next, links, savedTemplate, result]);

  const blocked = !result || !!result.error || result.errors.length > 0 || overLimit;
  const word = result?.format === "day" ? "day" : "module";

  const apply = () => {
    if (blocked) return;
    onApply({
      format: result.format,
      groups: next,
      importedCount: imported.length,
      mode: effectiveMode,
    });
  };

  const stats = result && !result.error ? result.stats : null;
  const statsLine = stats && (result.format === "day"
    ? `${stats.days} days · ${stats.topics} topics · ${formatMinutes(stats.minutes)}`
    : `${stats.modules} modules · ${stats.topics} topics · ${stats.subtopics} subtopics · ${formatMinutes(stats.minutes)}`);

  const footer = (
    <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
      {result && (
        <AppButton variant="ghost" className="tw-mr-auto" onClick={reset}>
          <ChevronLeft size={16} /> Choose another
        </AppButton>
      )}
      <AppButton variant="outline" onClick={onHide}>Cancel</AppButton>
      {result && !result.error && (
        <AppButton onClick={apply} disabled={blocked}>
          Load into editor
        </AppButton>
      )}
    </div>
  );

  return (
    <AppModal
      show={show}
      onHide={onHide}
      size="lg"
      icon={<FileSpreadsheet size={18} />}
      title="Import syllabus"
      footer={footer}
    >
      {!result && (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-m-0 tw-text-sm tw-text-content-muted">
            Bring in a whole syllabus at once from Excel, Google Sheets or a CSV — or paste it. You'll see what was
            read before anything changes, and nothing is saved until you press Save &amp; Activate.
          </p>

          <Tabs value={source} onChange={setSource}>
            <TabsList>
              <TabsTrigger value="file"><Upload size={14} /> Upload a file</TabsTrigger>
              <TabsTrigger value="paste">Paste</TabsTrigger>
            </TabsList>
            <TabsContent value="file" className="tw-mt-3">
              <FileUpload
                files={files}
                onChange={readFile}
                accept={ACCEPTED_EXTENSIONS}
                multiple={false}
                maxFiles={1}
                maxSizeMB={MAX_FILE_MB}
                disabled={reading}
                hint={reading ? "Reading…" : "Drop an .xlsx, .xls or .csv file here, or click to choose"}
              />
            </TabsContent>
            <TabsContent value="paste" className="tw-mt-3 tw-flex tw-flex-col tw-gap-2">
              <FormField
                label="Syllabus text"
                hint="Cells copied from a spreadsheet keep their columns. In an outline, start modules with “Module 1:” (or days with “Day 1:”), and indent subtopics under their topic."
              >
                <Textarea
                  rows={10}
                  value={pasted}
                  onChange={(e) => setPasted(e.target.value)}
                  placeholder={PASTE_PLACEHOLDER}
                  className="tw-font-mono tw-text-[13px]"
                />
              </FormField>
              <div>
                <AppButton onClick={readPasted} disabled={!pasted.trim()}>Read pasted text</AppButton>
              </div>
            </TabsContent>
          </Tabs>

          <section aria-label="Templates" className="tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-3">
            <span className="tw-text-[13px] tw-font-semibold tw-text-content">Start from a template</span>
            <span className="tw-text-xs tw-text-content-muted">
              Each template has sample rows and an Instructions sheet. Keep the header row; the column order doesn't matter.
            </span>
            {["module", "day"].map((f) => (
              <div key={f} className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <span className="tw-inline-flex tw-min-w-[13rem] tw-items-center tw-gap-1.5 tw-text-[13px] tw-text-content">
                  {f === "module" ? <Boxes size={14} /> : <CalendarDays size={14} />}
                  {FORMAT_LABEL[f]}
                </span>
                <AppButton size="xs" variant="outline" onClick={() => downloadTemplate(f, "xlsx")}>
                  <Download size={13} /> Excel
                </AppButton>
                <AppButton size="xs" variant="outline" onClick={() => downloadTemplate(f, "csv")}>
                  <Download size={13} /> CSV
                </AppButton>
              </div>
            ))}
          </section>
        </div>
      )}

      {result?.error && (
        <Callout tone="danger" role="alert">{result.error}</Callout>
      )}

      {result && !result.error && (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <span className="tw-text-xs tw-text-content-subtle">{sourceLabel}</span>
            <span className="tw-text-sm tw-font-semibold tw-text-content">
              {FORMAT_LABEL[result.format]} · {statsLine}
            </span>
          </div>

          {result.errors.length > 0 && (
            <Callout tone="danger" role="alert">
              <p className="tw-m-0 tw-mb-1.5 tw-text-[13px] tw-font-semibold">
                Fix {result.errors.length === 1 ? "this" : `these ${result.errors.length} problems`} in the file and import it again:
              </p>
              <IssueList items={result.errors} unit={result.unit} />
            </Callout>
          )}

          {overLimit && (
            <Callout tone="danger" role="alert">
              Together with the {existing.length} already in the editor that's {next.length} {word}s — the most a
              syllabus can hold is {combinedLimit}. Replace instead, or import fewer.
            </Callout>
          )}

          {result.format !== currentFormat && result.errors.length === 0 && (
            <Callout tone="info">
              This is a {FORMAT_LABEL[result.format]} syllabus, so the editor switches to that format. What's in the
              {" "}{FORMAT_LABEL[currentFormat]} editor now is kept until you leave the page, and every saved version
              stays under Versions.
            </Callout>
          )}

          {result.warnings.length > 0 && (
            <Callout tone="warning">
              <p className="tw-m-0 tw-mb-1.5 tw-text-[13px] tw-font-semibold">Read with {result.warnings.length === 1 ? "one note" : `${result.warnings.length} notes`}:</p>
              <IssueList items={result.warnings} unit={result.unit} />
            </Callout>
          )}

          {canAppend && result.errors.length === 0 && (
            <fieldset className="tw-m-0 tw-flex tw-flex-col tw-gap-2 tw-border-0 tw-p-0">
              <legend className="tw-mb-1 tw-p-0 tw-text-[13px] tw-font-semibold tw-text-content">
                The editor already has {existing.length} {word}{existing.length === 1 ? "" : "s"}
              </legend>
              <Radio
                name={modeName}
                value="replace"
                checked={mode === "replace"}
                onChange={() => setMode("replace")}
                label="Replace them with the file"
                description={`The syllabus becomes exactly what's in the file — ${imported.length} ${word}${imported.length === 1 ? "" : "s"}.`}
              />
              <Radio
                name={modeName}
                value="append"
                checked={mode === "append"}
                onChange={() => setMode("append")}
                label="Add the file after them"
                description={`Numbered from ${word === "day" ? "Day" : "Module"} ${existing.length + 1}, so nothing already linked moves.`}
              />
            </fieldset>
          )}

          {impact.length > 0 && result.errors.length === 0 && (
            <Callout tone="warning">
              <p className="tw-m-0 tw-mb-1.5 tw-text-[13px] tw-font-semibold">
                Linked content will point somewhere new once you save:
              </p>
              <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0 tw-text-[13px]">
                {impact.map((it) => {
                  const what = [
                    it.materials ? `${it.materials} material${it.materials === 1 ? "" : "s"}` : null,
                    it.smartSessions ? `${it.smartSessions} Smart Session${it.smartSessions === 1 ? "" : "s"}` : null,
                  ].filter(Boolean).join(", ");
                  const label = savedTemplate?.syllabusType === "module" ? "Module" : "Day";
                  return (
                    <li key={it.number}>
                      <strong className="tw-font-semibold">{label} {it.number} “{it.title}”</strong>
                      {it.removed ? " is no longer in the syllabus" : it.nextTitle && it.nextTitle.toLowerCase() !== it.title.toLowerCase() ? ` becomes “${it.nextTitle}”` : "’s topics change"}
                      {" "}— {what}
                    </li>
                  );
                })}
              </ul>
              <p className="tw-m-0 tw-mt-1.5 tw-text-xs">
                They are linked by number and topic position. Choose “Add after” to keep them where they are, or relink
                them after saving.
              </p>
            </Callout>
          )}

          {links === false && result.errors.length === 0 && (
            <Callout tone="info">Couldn't check which materials are linked to the current syllabus.</Callout>
          )}

          {result.format === "day" && links?.startedBatches > 0 && result.errors.length === 0 && (
            <Callout tone="info">
              {links.startedBatches} batch{links.startedBatches === 1 ? " has" : "es have"} already started and keep
              {links.startedBatches === 1 ? "s" : ""} the day plan {links.startedBatches === 1 ? "it" : "they"} started with.
            </Callout>
          )}

          {result.errors.length === 0 && <PreviewTree result={result} />}
        </div>
      )}
    </AppModal>
  );
}
